import { useCallback, useEffect, useState } from "react";
import loader from "../../../../../components/recharts/loader.gif";
import { ChevronUp, ChevronDown } from "lucide-react";
import { getBessResultSummary } from "../../../../../Rest_api/RestApi";

interface SummaryData {
  year: number;
  period: string;
  max_deficit: string | number;
  peak_demand: string | number;
  surplus_deficit_at_pd: string | number;
  percent_re_share_gen: string | number;
  bess_sur_consumed: string | number;
  bess_def_bridged: string | number;
}

interface MonthlyData {
  year: number;
  month: number;
  month_name: string;
  period: string;
  max_deficit: string | number;
  peak_demand: string | number;
  surplus_deficit_at_pd: string | number;
  percent_re_share_gen: string | number;
  bess_sur_consumed: string | number;
  bess_def_bridged: string | number;
}

const HeaderCell: React.FC<{ title: React.ReactNode; className?: string }> = ({ title, className = "" }) => (
  <th className={`py-2 text-[12px] px-2 text-center font-medium text-[#F1935C] leading-tight ${className}`}>
    {title}
  </th>
);

const HeaderCell2: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <th className="py-2 text-[12px] px-2 text-center font-medium text-[#34656D] !bg-[#F7FAFF] leading-tight">
    {title}
  </th>
);

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function QuantumSummaryTable({
  refreshKey,
  setError,
  selectedMonths,
  setSelectedMonths,
  setAvailableMonths,
}: {
  refreshKey: number;
  setError?: any;
  selectedMonths: any[];
  setSelectedMonths: (months: any[]) => void;
  setAvailableMonths: (months: any[]) => void;
}) {
  const [data, setData] = useState<SummaryData[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // Helper function to parse numeric values
  const parseValue = (value: string | number): number => {
    if (typeof value === "number") return value;
    return parseFloat(value) || 0;
  };

  // Fetch data from API
  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getBessResultSummary();



      if (response && response.data_yearwise && response.data_monthwise) {
        setData(response.data_yearwise);
        setMonthlyData(response.data_monthwise);

        // Extract unique months from monthly data and set available months
        const uniqueMonths = Array.from(
          new Set(
            response.data_monthwise.map(
              (item: MonthlyData) => item.month_name
            )
          )
        );
        const monthOptions = uniqueMonths.map((month) => ({
          value: month,
          label: month,
        }));
        setAvailableMonths(monthOptions);
      } else {
        console.error("Invalid response format:", response);
        setError?.("Invalid data format received from API.");
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setError?.("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [setError, setAvailableMonths]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  // Auto-expand all years when months are selected
  useEffect(() => {
    if (selectedMonths.length > 0) {
      setExpandedRow(-1); // -1 means all rows expanded
    } else {
      setExpandedRow(null);
    }
  }, [selectedMonths]);

  const handleYearClick = (year: number, index: number) => {
    if (expandedRow === index) {
      // Collapse if already expanded
      setExpandedRow(null);
    } else {
      // Expand
      setExpandedRow(index);
    }
  };

  // Helper function to check if a row should be expanded
  const isRowExpanded = (index: number) => {
    return expandedRow === -1 || expandedRow === index;
  };

  // Get filtered monthly data for a specific year
  const getFilteredMonthlyDataForYear = (year: number) => {
    const yearMonthlyData = monthlyData.filter((item) => item.year === year);

    if (selectedMonths.length > 0) {
      const selectedMonthNames = selectedMonths.map((m: any) => m.value ?? m);
      return yearMonthlyData.filter((item) =>
        selectedMonthNames.includes(item.month_name)
      );
    }
    return yearMonthlyData;
  };

  return (
    <div
      className="overflow-auto capitalize custom-scroll"
      style={{ maxHeight: "calc(40vh - 0px)" }}
    >
      {loading ? (
        <div className="h-full w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : (
        <>
          {data.length === 0 ? (
            <div className="flex items-center justify-center overflow-auto capitalize rounded-lg w-full h-full">
              <h1 className="chartHeading !text-base text-center !font-medium w-100">
                No Data Available...
              </h1>
            </div>
          ) : (
            <table id="summary-table" className="w-full table-fixed">
              <thead className="sticky top-0 z-[1] bg-white">
                <tr className="text-sm border-gray-200">
                  <th className="py-2 text-[12px] px-2 text-left font-medium text-[#F1935C] w-[4%]"></th>
                  <HeaderCell className="w-[12%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Year
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Max Deficit (MW)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Peak Demand (MW)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Surplus/Deficit Peak Demand (MW)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        % Gen RE Share
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        BESS Surplus Consumed (MWh)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        BESS Deficit Bridged (MWh)
                      </span>
                    }
                  />
                </tr>
              </thead>
              <tbody className="text-[#8C8A96] font-normal text-[12px]">
                {data.map((item, idx) => (
                  <>
                    <tr
                      key={idx}
                      className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${isRowExpanded(idx) ? "border-none" : ""
                        } ${idx % 2 === 0 ? "" : "!bg-[#FBFCFD]"}`}
                      onClick={() => handleYearClick(item.year, idx)}
                    >
                      <td className="py-2 px-2 text-center cursor-pointer">
                        {isRowExpanded(idx) ? (
                          <ChevronUp size={20} className="text-[#F1935C]" />
                        ) : (
                          <ChevronDown size={20} className="text-[#F1935C]" />
                        )}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal font-medium ${isRowExpanded(idx)
                          ? "text-[#525062]"
                          : "text-[#F1935C]"
                          }`}
                      >
                        {item.year}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.max_deficit).toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.peak_demand).toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.surplus_deficit_at_pd).toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.percent_re_share_gen).toFixed(2)}%
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.bess_sur_consumed).toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.bess_def_bridged).toFixed(2)}
                      </td>
                    </tr>

                    {/* Expanded Monthly Data - Accordion Style */}
                    {isRowExpanded(idx) && (
                      <tr>
                        <td colSpan={8} className="bg-[#F7FAFF] p-2">
                          <div
                            className="capitalize text-nowrap !border !border-[#F1935C] rounded-xl overflow-hidden"
                          >
                            <table className="w-full table-fixed">
                              <tbody className="text-[#8C8A96] font-normal text-[12px]">
                                {getFilteredMonthlyDataForYear(item.year)
                                  .length > 0 ? (
                                  getFilteredMonthlyDataForYear(item.year).map(
                                    (monthItem, monthIdx) => (
                                      <tr
                                        key={monthIdx}
                                        className="text-center [&>td]:border-none bg-[#F7FAFF] rounded-xl"
                                      >
                                        <td className="py-2 px-2 text-center whitespace-normal font-medium text-[#34656D] w-[4%]"></td>
                                        <td className="py-2 px-2 text-center whitespace-normal font-medium text-[#34656D] w-[12%]">
                                          {monthItem.month_name}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            monthItem.max_deficit
                                          ).toFixed(2)}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            monthItem.peak_demand
                                          ).toFixed(2)}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            monthItem.surplus_deficit_at_pd
                                          ).toFixed(2)}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            monthItem.percent_re_share_gen
                                          ).toFixed(2)}
                                          %
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            monthItem.bess_sur_consumed
                                          ).toFixed(2)}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            monthItem.bess_def_bridged
                                          ).toFixed(2)}
                                        </td>
                                      </tr>
                                    )
                                  )
                                ) : (
                                  <tr>
                                    <td colSpan={7} className="text-center py-8">
                                      <h1 className="chartHeading !text-base text-center !font-medium w-full">
                                        No Monthly Data Available...
                                      </h1>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}